import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Alert,
  Button,
  Spinner,
} from "react-bootstrap";
import { useParams, useHistory } from "react-router-dom";
import { Icon } from "../../utils/iconHelper";
import CourseManagementService, {
  CourseDocument,
  CourseRagIndex,
} from "../../_services/CourseManagementService";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import "./course-management.css";

const CourseDocumentsPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const history = useHistory();
  const courseIdNum = parseInt(courseId, 10);

  useDocumentTitle("Course Documents");

  const [documents, setDocuments] = useState<CourseDocument[]>([]);
  const [ragIndex, setRagIndex] = useState<CourseRagIndex | null>(null);
  const [courseName, setCourseName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ragify state
  const [ragifying, setRagifying] = useState(false);

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState<CourseDocument | null>(null);

  useEffect(() => {
    loadCourseName();
    fetchDocuments();
  }, [courseIdNum]);

  const loadCourseName = async () => {
    const res = await CourseManagementService.getCourseById(courseIdNum);
    if (res.success && res.data) {
      setCourseName(res.data.name);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await CourseManagementService.getCourseDocuments(courseIdNum);
      if (res.success) {
        setDocuments(res.data.documents);
        setRagIndex(res.data.ragIndex);
      } else {
        setError("Failed to fetch documents");
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError(`Invalid file format: "${file.name}". Only PDF files are allowed.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const res = await CourseManagementService.uploadCourseDocument(courseIdNum, file);
      if (res.success) {
        setSuccess(`"${file.name}" uploaded successfully`);
        fetchDocuments();
      } else {
        setError(res.message || "Upload failed");
      }
    } catch (err: any) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemove = (doc: CourseDocument) => {
    setDeletingDoc(doc);
    setShowDeleteModal(true);
  };

  const confirmRemove = async () => {
    if (!deletingDoc) return;
    try {
      const res = await CourseManagementService.removeCourseDocument(courseIdNum, deletingDoc.studyResourceId);
      if (res.success) {
        setSuccess("Document removed from course");
        setShowDeleteModal(false);
        setDeletingDoc(null);
        fetchDocuments();
      } else {
        setError(res.message || "Failed to remove document");
      }
    } catch (err: any) {
      setError(err.message || "Failed to remove document");
    }
  };

  const handleRagify = async () => {
    setRagifying(true);
    setError(null);
    try {
      const res = await CourseManagementService.ragifyCourseDocuments(courseIdNum);
      if (res.success) {
        const data = res.data;
        setSuccess(
          `RAG indexing complete: ${data.processedResources}/${data.totalResources} resources processed, ${data.totalVectors} vectors created`
        );
        fetchDocuments();
      } else {
        setError(res.message || "RAG indexing failed");
      }
    } catch (err: any) {
      setError(err.message || "RAG indexing failed");
    } finally {
      setRagifying(false);
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return "\u2014";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const ragStatusClass = (status: string | null) => {
    if (status === "completed") return "cm-rag-status-completed";
    if (status === "processing") return "cm-rag-status-processing";
    if (status === "failed") return "cm-rag-status-failed";
    return "cm-rag-status-none";
  };

  return (
    <div className="cm-page">
      {/* Header */}
      <div className="cm-header">
        <div className="cm-header-with-back">
          <button className="cm-btn-back" onClick={() => history.push("/course-management")}>
            <Icon.ArrowLeft size={16} />
            Back
          </button>
          <div>
            <h1>Course Documents</h1>
            <p className="cm-header-subtitle">{courseName || `Course #${courseIdNum}`}</p>
          </div>
        </div>
        <div className="cm-header-actions">
          <button
            className="cm-btn-create"
            onClick={handleUploadClick}
            disabled={uploading}
          >
            {uploading ? (
              <Spinner role="status" animation="border" size="sm" />
            ) : (
              <Icon.Upload size={16} />
            )}
            Upload PDF
          </button>
          <button
            className="cm-btn-secondary"
            onClick={handleRagify}
            disabled={ragifying || documents.length === 0}
          >
            {ragifying ? (
              <Spinner role="status" animation="border" size="sm" />
            ) : (
              <Icon.Zap size={16} />
            )}
            Ragify All
          </button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="application/pdf,.pdf"
        onChange={handleFileSelected}
      />

      {/* Alerts */}
      {error && (
        <div className="cm-alert cm-alert-error">
          <span>{error}</span>
          <button className="cm-alert-dismiss" onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {success && (
        <div className="cm-alert cm-alert-success">
          <span>{success}</span>
          <button className="cm-alert-dismiss" onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      {/* RAG Index Status */}
      {ragIndex && (
        <div className="cm-rag-bar">
          <span className="cm-rag-label">RAG Status:</span>
          <span className={ragStatusClass(ragIndex.status)}>
            {ragIndex.status.toUpperCase()}
          </span>
          <span className="cm-rag-meta">
            {ragIndex.processedResources}/{ragIndex.totalResources} resources
          </span>
          <span className="cm-rag-meta">
            {ragIndex.totalVectors} vectors
          </span>
          {ragIndex.processingCompletedAt && (
            <span className="cm-rag-meta">
              Last indexed: {new Date(ragIndex.processingCompletedAt).toLocaleString()}
            </span>
          )}
          {ragIndex.errorSummary && (
            <span className="cm-rag-error" title={ragIndex.errorSummary}>
              <Icon.AlertCircle size={14} />
              Errors
            </span>
          )}
        </div>
      )}

      {/* Documents Table */}
      <div className="cm-table-container">
        {loading ? (
          <div className="cm-loading">
            <Spinner role="status" animation="border" variant="primary" />
            <p>Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="cm-empty">
            <Icon.FileText size={48} style={{ color: "var(--text-faint)" }} />
            <h5>No documents yet</h5>
            <p>Upload PDFs (syllabi, schedules, reference material) to this course</p>
            <button className="cm-btn-create" onClick={handleUploadClick}>
              <Icon.Upload size={16} />
              Upload PDF
            </button>
          </div>
        ) : (
          <table className="cm-table">
            <thead>
              <tr>
                <th>Document</th>
                <th style={{ width: 100 }}>Type</th>
                <th style={{ width: 80 }}>Size</th>
                <th style={{ width: 110 }}>RAG Status</th>
                <th style={{ width: 120 }}>Uploaded</th>
                <th style={{ width: 70 }} className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.studyResourceId}>
                  <td>
                    <div className="cm-doc-name-cell">
                      <Icon.FileText size={16} className="cm-doc-icon" />
                      <div>
                        <div className="cm-doc-name">{doc.Name}</div>
                        {doc.URL && (
                          <a
                            href={doc.URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cm-doc-link"
                          >
                            View file
                          </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="cm-cell-muted">{doc.typeName || "PDF"}</td>
                  <td className="cm-cell-muted">{formatFileSize(doc.FileSizeInByte)}</td>
                  <td>
                    <span className={ragStatusClass(doc.ragStatus)}>
                      {doc.ragStatus ? doc.ragStatus : "Not indexed"}
                    </span>
                    {doc.chunksCreated ? (
                      <div className="cm-chunks-count">{doc.chunksCreated} chunks</div>
                    ) : null}
                  </td>
                  <td className="cm-cell-muted">
                    {doc.CreatedDate
                      ? new Date(doc.CreatedDate).toLocaleDateString()
                      : "\u2014"}
                  </td>
                  <td>
                    <div className="cm-actions">
                      <button
                        className="cm-btn-action cm-btn-delete"
                        onClick={() => handleRemove(doc)}
                        title="Remove from course"
                      >
                        <Icon.Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Remove this document from the course?</p>
          {deletingDoc && (
            <Alert variant="warning" className="mb-0">
              <strong>{deletingDoc.Name}</strong>
            </Alert>
          )}
          <p className="mt-3 text-muted small">
            The document will be unlinked from this course. The file itself will not be deleted.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmRemove}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseDocumentsPage;
